/**
 * SuperAdmin DemosTab — Vague 113 Wave 1m.
 *
 * Full pipeline view of all demo orgs:
 *   - Kanban board (Inscrit → Active → Engagé → Converti / Perdu)
 *   - Compact list with countdown + engagement score
 *   - Detail drawer with contact info, notes, audit, actions
 *
 * Actions per demo: Extend +7j, Convert to paid (with plan), Suspend, Notes.
 *
 * Endpoints:
 *   GET  /api/superadmin/demos                  → list
 *   GET  /api/superadmin/demos/{id}             → detail (refreshes score)
 *   POST /api/superadmin/demos/{id}/convert     → status='active'
 *   POST /api/superadmin/demos/{id}/extend      → +N days
 *   POST /api/superadmin/demos/{id}/stage       → kanban move
 *   POST /api/superadmin/demos/{id}/suspend
 *   POST /api/superadmin/demos/{id}/reactivate
 *   GET/POST/DELETE /api/superadmin/demos/{id}/notes
 *   GET  /api/superadmin/demos/extension-requests
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, Sparkles, Phone, Mail, Building2, FileText, Clock, TrendingUp,
  X, ChevronRight, Plus, Trash2, AlertTriangle, CheckCircle2, RotateCw,
  MessageSquare, Send, Bell,
} from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';

const STAGES = [
  { id: 'inscrit', label: 'Inscrit', color: 'bg-[#EEF2FF] text-[#3730A3] border-[#C7D2FE]' },
  { id: 'active', label: 'Activé', color: 'bg-[#E0F2FE] text-[#0369A1] border-[#7DD3FC]' },
  { id: 'engage', label: 'Engagé', color: 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]' },
  { id: 'converti', label: 'Converti', color: 'bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]' },
  { id: 'perdu', label: 'Perdu', color: 'bg-[#F3F4F8] text-[#6B6C85] border-[#E5E7EB]' },
];

export default function DemosTab() {
  const [view, setView] = useState('kanban'); // 'kanban' | 'list'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [pendingExt, setPendingExt] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: ext }, { data: notif }] = await Promise.all([
        api.get('/superadmin/demos?status=all'),
        api.get('/superadmin/demos/extension-requests?status=pending').catch(() => ({ data: { items: [] } })),
        api.get('/superadmin/notifications?unread_only=true&limit=20').catch(() => ({ data: { items: [] } })),
      ]);
      setItems(list.items || []);
      setPendingExt(ext.items || []);
      setNotifications(notif.items || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Chargement impossible' });
    } finally { setLoading(false); }
  };

  const markAllRead = async () => {
    try {
      await api.post('/superadmin/notifications/mark-read', {});
      setNotifications([]);
      toast({ title: 'OK', description: 'Notifications marquées comme lues' });
    } catch (err) {
      toast({ title: 'Erreur', description: 'Action impossible' });
    }
  };

  useEffect(() => { reload(); }, []);

  const stageBuckets = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s.id, []]));
    items.forEach((it) => {
      const k = STAGES.find((s) => s.id === it.kanban_stage)?.id || 'inscrit';
      map[k].push(it);
    });
    return map;
  }, [items]);

  if (loading) return <div className="flex items-center gap-2 text-[#6B6C85]" data-testid="demos-tab-loading"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;

  return (
    <div className="space-y-4" data-testid="demos-tab">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-white border border-[#EFF0F6] rounded-lg p-1">
          <button
            data-testid="demos-view-kanban"
            onClick={() => setView('kanban')}
            className={`h-8 px-3 rounded text-[12px] font-semibold ${view === 'kanban' ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85]'}`}
          >Pipeline (Kanban)</button>
          <button
            data-testid="demos-view-list"
            onClick={() => setView('list')}
            className={`h-8 px-3 rounded text-[12px] font-semibold ${view === 'list' ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85]'}`}
          >Liste</button>
        </div>
        <div className="text-[12px] text-[#6B6C85]">
          {items.length} demo{items.length > 1 ? 's' : ''}
          {pendingExt.length > 0 && (
            <span className="ml-3 inline-flex items-center gap-1 text-[#92400E] font-semibold">
              <Bell className="w-3 h-3" /> {pendingExt.length} demande{pendingExt.length > 1 ? 's' : ''} de prolongation en attente
            </span>
          )}
        </div>
        {/* Notifications bell */}
        <div className="relative">
          <button
            onClick={() => setShowNotifs(!showNotifs)}
            data-testid="demos-notifications-bell"
            className="h-8 px-2.5 rounded-lg border border-[#EFF0F6] text-[12px] text-[#4A4B63] hover:bg-[#FAFAFE] inline-flex items-center gap-1.5 relative"
          >
            <Bell className="w-3.5 h-3.5" />
            {notifications.length > 0 && (
              <span data-testid="demos-notifications-count" className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                {notifications.length}
              </span>
            )}
          </button>
          {showNotifs && (
            <div
              data-testid="demos-notifications-panel"
              className="absolute right-0 top-10 z-20 w-96 max-h-[500px] overflow-y-auto bg-white border border-[#EFF0F6] rounded-xl shadow-2xl"
            >
              <div className="sticky top-0 bg-white px-4 py-3 border-b border-[#EFF0F6] flex items-center justify-between">
                <span className="text-[13px] font-bold text-[#1A1B2E]">
                  Notifications ({notifications.length})
                </span>
                {notifications.length > 0 && (
                  <button
                    onClick={markAllRead}
                    data-testid="demos-notifications-mark-all"
                    className="text-[11px] text-[#4F46E5] hover:underline font-semibold"
                  >
                    Tout marquer comme lu
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-[12px] text-[#9CA3AF]">
                  Aucune notification non lue
                </div>
              ) : (
                <ul>
                  {notifications.map((n) => (
                    <li
                      key={n.id}
                      data-testid={`demos-notification-${n.id}`}
                      className="px-4 py-3 border-b border-[#EFF0F6] hover:bg-[#FAFAFE] cursor-pointer"
                      onClick={() => {
                        if (n.metadata?.org_id) {
                          const found = items.find((it) => it.id === n.metadata.org_id);
                          if (found) setSelected(found);
                        }
                        setShowNotifs(false);
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-[#4F46E5] mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{n.title}</div>
                          <div className="text-[11.5px] text-[#6B6C85] mt-0.5 line-clamp-2">{n.body}</div>
                          <div className="text-[10.5px] text-[#9CA3AF] mt-1">
                            {new Date(n.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <button
          onClick={reload}
          data-testid="demos-reload"
          className="h-8 px-2.5 rounded-lg border border-[#EFF0F6] text-[12px] text-[#4A4B63] hover:bg-[#FAFAFE] inline-flex items-center gap-1"
        >
          <RotateCw className="w-3 h-3" /> Rafraîchir
        </button>
      </div>

      {/* Kanban */}
      {view === 'kanban' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3" data-testid="demos-kanban">
          {STAGES.map((stage) => (
            <div key={stage.id} className="bg-white border border-[#EFF0F6] rounded-xl p-3">
              <div className={`flex items-center justify-between px-2 py-1 rounded-md border ${stage.color} mb-2`}>
                <span className="text-[12px] font-bold">{stage.label}</span>
                <span className="text-[10.5px] font-semibold">{stageBuckets[stage.id].length}</span>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {stageBuckets[stage.id].length === 0 ? (
                  <div className="text-[11px] text-[#9CA3AF] text-center py-3">Vide</div>
                ) : stageBuckets[stage.id].map((d) => (
                  <DemoCard key={d.id} demo={d} onClick={() => setSelected(d)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List */}
      {view === 'list' && (
        <div className="bg-white border border-[#EFF0F6] rounded-xl overflow-hidden" data-testid="demos-list">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#FAFAFE] text-[#6B6C85]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Organisme</th>
                <th className="text-left px-3 py-2 font-semibold">Contact</th>
                <th className="text-left px-3 py-2 font-semibold">Statut</th>
                <th className="text-left px-3 py-2 font-semibold">Étape</th>
                <th className="text-left px-3 py-2 font-semibold">Jours restants</th>
                <th className="text-left px-3 py-2 font-semibold">Score</th>
                <th className="text-right px-3 py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-[#EFF0F6] hover:bg-[#FAFAFE] cursor-pointer" onClick={() => setSelected(d)} data-testid={`demos-row-${d.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-[#1A1B2E]">{d.name}</div>
                    <div className="text-[10.5px] text-[#9CA3AF] flex items-center gap-1.5">
                      <span>{d.id}</span>
                      {d.demo_seed_mode === 'blank' && (
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[9.5px] font-bold">
                          VIERGE
                        </span>
                      )}
                      {d.demo_seed_mode === 'discovery' && (
                        <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[9.5px] font-bold">
                          DÉCOUVERTE
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div>{d.owner_name || '—'}</div>
                    <div className="text-[10.5px] text-[#6B6C85]">{d.owner_email}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusBadge status={d.subscription_status} />
                  </td>
                  <td className="px-3 py-2.5"><StageBadge stage={d.kanban_stage} /></td>
                  <td className="px-3 py-2.5">
                    {d.subscription_status === 'demo' ? <CountdownPill days={d.days_left} /> : <span className="text-[11px] text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="font-bold text-[#4F46E5]">{d.engagement_score}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <ChevronRight className="w-4 h-4 text-[#9CA3AF] inline-block" />
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-[#9CA3AF]">Aucune demo pour le moment.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <DemoDetailDrawer
          orgId={selected.id}
          onClose={() => setSelected(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
}

// ===========================================================================
function DemoCard({ demo, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`demo-card-${demo.id}`}
      className="w-full text-left bg-[#FAFAFE] hover:bg-white border border-[#EFF0F6] rounded-lg p-2.5 transition-colors"
    >
      <div className="text-[12.5px] font-bold text-[#1A1B2E] truncate">{demo.name}</div>
      <div className="text-[10.5px] text-[#6B6C85] truncate">{demo.owner_email}</div>
      <div className="mt-2 flex items-center gap-2 text-[10.5px] text-[#6B6C85]">
        {demo.subscription_status === 'demo' && <CountdownPill days={demo.days_left} compact />}
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5] font-bold">
          <TrendingUp className="w-3 h-3" />{demo.engagement_score}
        </span>
      </div>
    </button>
  );
}

function StatusBadge({ status }) {
  const map = {
    demo: ['bg-[#EEF2FF] text-[#3730A3]', 'Demo'],
    active: ['bg-[#DCFCE7] text-[#15803D]', 'Actif (payant)'],
    trial_expired: ['bg-[#FEF3C7] text-[#92400E]', 'Expiré (lecture seule)'],
    suspended: ['bg-[#FEE2E2] text-[#991B1B]', 'Suspendu'],
  };
  const [klass, label] = map[status] || ['bg-[#F3F4F8] text-[#6B6C85]', status];
  return <span className={`px-1.5 py-0.5 rounded text-[10.5px] font-bold ${klass}`}>{label}</span>;
}

function StageBadge({ stage }) {
  const found = STAGES.find((s) => s.id === stage) || STAGES[0];
  return <span className={`px-1.5 py-0.5 rounded text-[10.5px] font-bold border ${found.color}`}>{found.label}</span>;
}

function CountdownPill({ days, compact = false }) {
  if (days === null || days === undefined) return null;
  const tone =
    days < 0 ? 'bg-[#FEE2E2] text-[#991B1B]' :
    days <= 1 ? 'bg-[#FEE2E2] text-[#991B1B]' :
    days <= 3 ? 'bg-[#FEF3C7] text-[#92400E]' :
                'bg-[#EEF2FF] text-[#3730A3]';
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded ${tone} font-bold text-[10.5px]`}>
      <Clock className="w-3 h-3" />
      {days < 0 ? `${Math.abs(days)}j dépassés` : `${days}j${compact ? '' : ' restants'}`}
    </span>
  );
}

// ===========================================================================
// Detail drawer
// ===========================================================================
function DemoDetailDrawer({ orgId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState('');
  const [working, setWorking] = useState(false);
  const [plan, setPlan] = useState('Pro');
  const [extDays, setExtDays] = useState(7);

  const reload = async () => {
    try {
      const [{ data: d }, { data: ns }] = await Promise.all([
        api.get(`/superadmin/demos/${orgId}`),
        api.get(`/superadmin/demos/${orgId}/notes`),
      ]);
      setDetail(d);
      setNotes(ns.items || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Détail introuvable' });
    }
  };

  useEffect(() => { reload(); }, [orgId]);

  const act = async (path, payload) => {
    setWorking(true);
    try {
      await api.post(`/superadmin/demos/${orgId}${path}`, payload || {});
      // Wave 1n — explicit success toast for conversion
      if (path === '/convert') {
        toast({
          title: '🎉 Démo convertie en tenant actif',
          description: `${detail?.org?.name || 'L\'organisation'} apparaît maintenant dans l'onglet Tenants (plan ${payload?.plan || 'Pro'}). Un email de bienvenue a été envoyé.`,
        });
      } else {
        toast({ title: 'Action réalisée' });
      }
      await reload();
      onChanged?.();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setWorking(false); }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setWorking(true);
    try {
      await api.post(`/superadmin/demos/${orgId}/notes`, { body: noteBody });
      setNoteBody('');
      await reload();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setWorking(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      await api.delete(`/superadmin/demos/${orgId}/notes/${noteId}`);
      await reload();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    }
  };

  if (!detail) {
    return (
      <div className="fixed inset-0 z-50 flex" data-testid="demo-detail-drawer">
        <div className="flex-1 bg-black/40" onClick={onClose} />
        <div className="w-[500px] bg-white flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
      </div>
    );
  }

  const { org, owner, audit_recent: audit } = detail;
  const contact = org.demo_contact || {};

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="demo-detail-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full sm:w-[640px] bg-white overflow-y-auto">
        {/* Header */}
        <div className="brand-gradient text-white p-5 sticky top-0 z-10">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-wider opacity-90">
                <Sparkles className="w-3 h-3" /> Demo
              </div>
              <h2 className="text-[22px] font-bold mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{org.name}</h2>
              <div className="text-[12px] opacity-90 mt-1">{org.id}</div>
            </div>
            <button onClick={onClose} aria-label="Fermer" data-testid="demo-detail-close" className="w-9 h-9 rounded-full hover:bg-white/20 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <StatusBadge status={org.subscription_status} />
            <StageBadge stage={org.demo_kanban_stage} />
            {org.subscription_status === 'demo' && org.trial_expires_at && (
              <CountdownPill days={Math.ceil((new Date(org.trial_expires_at) - new Date()) / 86400000)} />
            )}
            <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 text-[11px] font-bold">
              <TrendingUp className="w-3 h-3" /> Score {org.demo_engagement_score || 0}
            </span>
          </div>
        </div>

        {/* Contact info */}
        <div className="p-5 border-b border-[#EFF0F6]">
          <h3 className="text-[12.5px] font-bold text-[#1A1B2E] mb-2 uppercase tracking-wider">Contact</h3>
          <div className="grid grid-cols-2 gap-2 text-[12.5px]">
            <ContactRow icon={Building2} label="Organisme" value={contact.owner_name || '—'} />
            <ContactRow icon={Mail} label="Email" value={contact.owner_email} clipboard />
            <ContactRow icon={Phone} label="Téléphone" value={contact.phone || '—'} clipboard={!!contact.phone} />
            <ContactRow icon={FileText} label="SIRET" value={contact.siret || '—'} />
          </div>
          {contact.objectives && (
            <div className="mt-3 p-2.5 rounded-lg bg-[#FAFAFE] border border-[#EFF0F6]">
              <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#6B6C85] mb-1">Objectifs</div>
              <div className="text-[12.5px] text-[#4A4B63]">{contact.objectives}</div>
            </div>
          )}
          {contact.utm_source && (
            <div className="mt-2 text-[11px] text-[#6B6C85]">Source d&apos;acquisition : <strong>{contact.utm_source}</strong></div>
          )}
        </div>

        {/* Actions */}
        <div className="p-5 border-b border-[#EFF0F6] space-y-3">
          <h3 className="text-[12.5px] font-bold text-[#1A1B2E] mb-1 uppercase tracking-wider">Actions</h3>

          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="number"
              min="1"
              max="365"
              value={extDays}
              onChange={(e) => setExtDays(parseInt(e.target.value || '7', 10))}
              data-testid="demo-extend-days-input"
              className="w-20 h-9 px-3 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px]"
            />
            <button
              onClick={() => act('/extend', { days: extDays })}
              disabled={working}
              data-testid="demo-action-extend"
              className="h-9 px-3 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold disabled:opacity-50"
            >
              ⏰ Prolonger
            </button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              data-testid="demo-convert-plan"
              className="h-9 px-2 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px]"
            >
              <option value="Starter">Starter</option>
              <option value="Pro">Pro</option>
              <option value="Enterprise">Enterprise</option>
            </select>
            <button
              onClick={() => act('/convert', { plan })}
              disabled={working}
              data-testid="demo-action-convert"
              className="h-9 px-3 rounded-lg bg-[#DCFCE7] text-[#15803D] text-[12.5px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Convertir en payant
            </button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {org.subscription_status !== 'suspended' ? (
              <button
                onClick={() => act('/suspend')}
                disabled={working}
                data-testid="demo-action-suspend"
                className="h-9 px-3 rounded-lg bg-[#FEE2E2] text-[#991B1B] text-[12.5px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
              >
                <AlertTriangle className="w-3.5 h-3.5" /> Suspendre
              </button>
            ) : (
              <button
                onClick={() => act('/reactivate')}
                disabled={working}
                data-testid="demo-action-reactivate"
                className="h-9 px-3 rounded-lg bg-[#EEF2FF] text-[#4F46E5] text-[12.5px] font-semibold disabled:opacity-50"
              >
                ↻ Réactiver
              </button>
            )}
            {STAGES.filter((s) => s.id !== org.demo_kanban_stage).map((s) => (
              <button
                key={s.id}
                onClick={() => act('/stage', { stage: s.id })}
                disabled={working}
                data-testid={`demo-stage-${s.id}`}
                className={`h-9 px-3 rounded-lg text-[11.5px] font-semibold border ${s.color} disabled:opacity-50`}
              >
                → {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="p-5 border-b border-[#EFF0F6]">
          <h3 className="text-[12.5px] font-bold text-[#1A1B2E] mb-2 uppercase tracking-wider flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" /> Notes ({notes.length})
          </h3>
          <div className="flex items-end gap-2 mb-3">
            <textarea
              rows={2}
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              placeholder="Ajouter une note (call, decision, follow-up date…)"
              data-testid="demo-note-input"
              className="flex-1 px-3 py-2 rounded-lg bg-[#F6F7FB] border border-[#EFF0F6] text-[12.5px] resize-none"
            />
            <button
              onClick={addNote}
              disabled={working || !noteBody.trim()}
              data-testid="demo-note-submit"
              className="h-9 px-3 rounded-lg brand-gradient text-white text-[12.5px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
            >
              <Send className="w-3 h-3" /> Envoyer
            </button>
          </div>
          <ul className="space-y-2 max-h-64 overflow-y-auto">
            {notes.length === 0 ? (
              <li className="text-[12px] text-[#9CA3AF] text-center py-3">Aucune note pour le moment.</li>
            ) : notes.map((n) => (
              <li key={n.id} className="rounded-lg border border-[#EFF0F6] bg-white p-2.5 flex items-start gap-2" data-testid={`demo-note-${n.id}`}>
                <div className="flex-1">
                  <div className="text-[12.5px] text-[#1A1B2E] whitespace-pre-wrap">{n.body}</div>
                  <div className="text-[10.5px] text-[#9CA3AF] mt-1">
                    {n.author_name || n.author_email} · {new Date(n.created_at).toLocaleString('fr-FR')}
                  </div>
                </div>
                <button
                  onClick={() => deleteNote(n.id)}
                  className="text-[#B91C1C] hover:bg-[#FEF2F2] p-1 rounded"
                  aria-label="Supprimer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Stats */}
        <div className="p-5 border-b border-[#EFF0F6]">
          <h3 className="text-[12.5px] font-bold text-[#1A1B2E] mb-2 uppercase tracking-wider">Activité récente</h3>
          {audit?.length ? (
            <ul className="space-y-1.5 text-[12px]">
              {audit.map((a, i) => (
                <li key={i} className="text-[#4A4B63]">
                  <span className="font-semibold">{a.action}</span> · {a.entity_type}
                  <span className="text-[10.5px] text-[#9CA3AF] ml-2">{a.created_at ? new Date(a.created_at).toLocaleString('fr-FR') : ''}</span>
                </li>
              ))}
            </ul>
          ) : <div className="text-[12px] text-[#9CA3AF]">Aucune activité récente.</div>}
        </div>
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, clipboard }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-[#6B6C85] mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#6B6C85]">{label}</div>
        <div className="text-[12.5px] text-[#1A1B2E] truncate flex items-center gap-1">
          <span className="truncate">{value || '—'}</span>
          {clipboard && value && value !== '—' && (
            <button
              onClick={() => { navigator.clipboard.writeText(value); toast({ title: 'Copié' }); }}
              className="text-[10px] text-[#4F46E5] hover:underline"
              aria-label="Copier"
            >📋</button>
          )}
        </div>
      </div>
    </div>
  );
}
